// Squad analytics, computed from board tasks (each task's squad, assignees,
// sprint, status and deadline). Product Backlog tasks and tasks without a
// sprint are not part of any sprint scope.

import { BACKLOG_STATUS } from "./constants";
import { currentSprint } from "./boardState";

export const ALL_SPRINTS = "all";

export const SQUAD_STATUSES = [
  { value: "sprint_backlog", label: "Sprint Backlog", color: "#94A3B8" },
  { value: "ready", label: "Ready", color: "#38BDF8" },
  { value: "in_progress", label: "In Progress", color: "#FF8A1F" },
  { value: "code_review", label: "Code Review", color: "#A78BFA" },
  { value: "qa_testing", label: "QA Testing", color: "#FACC15" },
  { value: "qa_rejected", label: "QA Rejected", color: "#EF4444" },
  { value: "done", label: "Done", color: "#22C55E" },
];

export const SQUAD_PRIORITIES = [
  { value: "critical", label: "Urgent", color: "#EF4444" },
  { value: "high", label: "High", color: "#FF8A1F" },
  { value: "medium", label: "Medium", color: "#FACC15" },
  { value: "low", label: "Low", color: "#94A3B8" },
];

export const priorityCounts = (tasks) =>
  Object.fromEntries(SQUAD_PRIORITIES.map((p) => [p.value, tasks.filter((t) => t.priority === p.value).length]));

const IN_FLIGHT = new Set(["ready", "in_progress", "code_review", "qa_testing", "qa_rejected"]);

const idOf = (v) => (v && typeof v === "object" ? v._id : v);

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Sprints oldest first. */
export const sortSprints = (sprints = []) =>
  [...sprints].sort((a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0));

export const LAST_3 = "last3";

/** The sprint ids a scope covers: one sprint id, LAST_3 (up to and including the current one) or ALL_SPRINTS. */
export const scopeIds = (scope, sprintList, currentId) => {
  if (scope === ALL_SPRINTS) return new Set(sprintList.map((s) => s._id));
  if (scope === LAST_3) {
    const end = sprintList.findIndex((s) => s._id === currentId);
    const upTo = end >= 0 ? sprintList.slice(0, end + 1) : sprintList;
    return new Set(upTo.slice(-3).map((s) => s._id));
  }
  return new Set([scope]);
};

const isOverdue = (task, currentId, today) =>
  idOf(task.sprint) === currentId &&
  task.status !== "done" &&
  Boolean(task.dueDate) &&
  startOfDay(new Date(task.dueDate)) < today;

/** Counts for a set of tasks: by status plus tasks / done / open / inFlight / overdue / pct. */
export const summarize = (tasks, currentId, now = new Date()) => {
  const today = startOfDay(now);
  const byStatus = Object.fromEntries(SQUAD_STATUSES.map((s) => [s.value, 0]));
  let overdue = 0;
  for (const t of tasks) {
    if (t.status in byStatus) byStatus[t.status] += 1;
    if (isOverdue(t, currentId, today)) overdue += 1;
  }
  const total = SQUAD_STATUSES.reduce((n, s) => n + byStatus[s.value], 0);
  const done = byStatus.done;
  const inFlight = [...IN_FLIGHT].reduce((n, k) => n + byStatus[k], 0);
  return {
    byStatus,
    tasks: total,
    done,
    open: total - done,
    inFlight,
    overdue,
    pct: total ? Math.round((done / total) * 100) : 0,
  };
};

/** Tasks that belong to a sprint (drops the Product Backlog). */
export const sprintTasks = (tasks) => tasks.filter((t) => t.status !== BACKLOG_STATUS && idOf(t.sprint));

/** Tasks in the selected scope (`ids` from scopeIds). */
export const inScope = (tasks, ids) => sprintTasks(tasks).filter((t) => ids.has(idOf(t.sprint)));

export const forAssignee = (tasks, employeeId) =>
  tasks.filter((t) => (t.assignees || []).some((a) => idOf(a) === employeeId));

/** The squad's sprint summary: per-sprint + a rollup, keyed for the list cards. */
export const squadOverview = (squadTasks, sprints, now = new Date()) => {
  const current = currentSprint(sprints, now);
  const all = sprintTasks(squadTasks);
  const cur = current ? summarize(all.filter((t) => idOf(t.sprint) === current._id), current._id, now) : null;
  return { current, currentStats: cur, allStats: summarize(all, current?._id, now) };
};

/** One bar per sprint for the velocity chart: planned vs done, flagged when in scope. */
export const velocity = (list, squadTasks, ids, currentId, now = new Date()) =>
  list.map((sprint) => {
    const stats = summarize(
      sprintTasks(squadTasks).filter((t) => idOf(t.sprint) === sprint._id),
      currentId,
      now,
    );
    return { sprint, tasks: stats.tasks, done: stats.done, inScope: ids.has(sprint._id) };
  });

const doneCount = (squadTasks, sprintId) =>
  sprintTasks(squadTasks).filter((t) => idOf(t.sprint) === sprintId && t.status === "done").length;

/** Mean done count of the sprints before the current one (the current one excluded). */
export const previousAverage = (squadTasks, sprintList, current) => {
  const previous = sprintList.filter((s) => s._id !== current._id && new Date(s.startDate) < new Date(current.startDate));
  const average = previous.length ? previous.reduce((n, s) => n + doneCount(squadTasks, s._id), 0) / previous.length : 0;
  const currentDone = doneCount(squadTasks, current._id);
  return {
    currentDone,
    average,
    previousCount: previous.length,
    delta: average > 0 ? Math.round(((currentDone - average) / average) * 100) : null,
  };
};

/** Top contributor, most open work and sprint pace for the selected scope. */
export const insights = ({ members, scopeTasks, squadTasks, sprintList, current, now = new Date() }) => {
  const perMember = members
    .filter((m) => m.employee)
    .map((m) => ({ employee: m.employee, ...summarize(forAssignee(scopeTasks, m.employee._id), current?._id, now) }));
  const best = (key) => {
    const top = [...perMember].sort((a, b) => b[key] - a[key])[0];
    return top && top[key] > 0 ? top : null;
  };

  const pace = current ? previousAverage(squadTasks, sprintList, current) : null;
  if (pace) pace.currentDone = doneCount(squadTasks, current._id);
  return { topContributor: best("done"), mostOpen: best("open"), pace };
};
